// "İşletmeler" bölümü — portal yetki v2 §8.1/Y6: "Tüm işletmeler" anahtarı (NULL) ya da onay
// kutulu işletme ağacı (grup kutusu altındaki yaprakları işaretler; yalnız yaprak id'leri gönderilir).
// Kısıtlı Admin: NULL alan kilitli ("Tam erişim — yalnız Süper Admin daraltabilir"), "Tüm işletmeler"
// seçilemez, kapsam dışı işletme adları gösterilmez ("+N kapsamınız dışında"). Tek yapraklı
// firmada bölüm hiç görünmez.

import type { ClientEntity } from "@mahir/shared";
import { CLIENT_ADMIN_COPY } from "../../lib/client-admin-copy";
import { leafEntityIds, toggleEntities, type SetDraftV2 } from "../../lib/client-access-draft-v2";
import { EntityCheckTree } from "../clients/EntityTree";

const C = CLIENT_ADMIN_COPY.drawer;

interface Props {
  setId: string;
  entities: ClientEntity[];
  hiddenCount: number;
  draft: SetDraftV2;
  disabled: boolean;
  /** Hedefin işletme alanı NULL ve Admin kısıtlı → salt-okunur. */
  lockedNull: boolean;
  /** Kısıtlı Admin "Tüm işletmeler"i (NULL) seçemez. */
  noAll: boolean;
  onChange: (next: SetDraftV2) => void;
}

export function AccessEntitiesSection(props: Props): JSX.Element | null {
  const { setId, entities, hiddenCount, draft, disabled, lockedNull, noAll, onChange } = props;
  const leafIds = leafEntityIds(entities);
  if (leafIds.length <= 1 && hiddenCount === 0 && !lockedNull) return null;
  const all = draft.allEntities || lockedNull;
  // Boş seçim yalnız kullanıcıyı işletmesiz bırakacaksa uyarılır (kapsam dışı işletmeleri varsa değil).
  const empty = !all && draft.entities.size === 0 && hiddenCount === 0;
  return (
    <section data-testid="access-entities" aria-labelledby={`ent-${setId}`}>
      <h4
        id={`ent-${setId}`}
        className="text-sm mb-1"
        style={{ color: "var(--color-text-2)", fontWeight: 600 }}
      >
        {C.entities.title}
      </h4>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={all}
          disabled={disabled || lockedNull || noAll}
          onChange={(e) =>
            onChange(
              e.target.checked
                ? { ...draft, allEntities: true }
                : { ...draft, allEntities: false, entities: new Set(leafIds) },
            )
          }
        />
        {C.entities.allToggle}
      </label>
      {lockedNull ? (
        <p className="text-xs mt-1" style={{ color: "var(--color-text-3)" }}>
          {C.scopeLocked.full}
        </p>
      ) : null}
      {!all ? (
        <div className="mt-1">
          <EntityCheckTree
            entities={entities}
            checked={draft.entities}
            disabled={disabled}
            ariaLabel={C.entities.treeAria}
            inactiveLabel={C.entities.inactive}
            onToggle={(ids, on) => onChange(toggleEntities(draft, ids, on))}
          />
          {empty ? (
            <p role="alert" className="text-xs mt-1" style={{ color: "var(--color-error)" }}>
              {C.entities.noneSelected}
            </p>
          ) : null}
        </div>
      ) : null}
      {hiddenCount > 0 ? (
        <p className="text-xs mt-1" style={{ color: "var(--color-text-3)" }}>
          {C.scopeLocked.hidden(hiddenCount)}
        </p>
      ) : null}
      <p className="text-xs mt-1" style={{ color: "var(--color-text-3)" }}>
        {C.entities.help}
      </p>
    </section>
  );
}
